import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SidebarTrigger } from '@doger/ui/components/ui/sidebar';
import { Skeleton } from '@doger/ui/components/ui/skeleton';
import { ThemeSwitcher } from '@doger/ui/theme';
import { ChatMessages, CurrentModel } from '@doger/core/modules/chat';
import { LanguageSwitcher } from '@doger/core/shared/i18n';

import {
  LogAmplitudeEventOnMount,
  useAnalytics,
} from '@doger/chat/shared/analytics';

import {
  useChatQuery,
  useSendMessageMutation,
  useSendMessagePending,
} from '../api';
import { ChatInput } from '../chat-input.component';
import styles from './conversation.module.scss';

export type ConversationProps = {
  sessionId: string;
};

const Conversation = memo(({ sessionId }: ConversationProps) => {
  const isSendMessageMutationPending = useSendMessagePending({ sessionId });
  const { t } = useTranslation();

  const sendMessageMutation = useSendMessageMutation();
  const { data: session, isLoading } = useChatQuery({ sessionId });
  const analytics = useAnalytics();

  if (isLoading) {
    return (
      <div className={styles.mainChat}>
        <div className={styles.header}>
          <SidebarTrigger />

          <div className={styles.modelSelectorSkeleton}>
            <Skeleton className={styles.modelSelectorSkeletonContent} />
          </div>

          <div className={styles.headerActions}>
            <ThemeSwitcher
              onThemeChange={(theme) =>
                analytics.amplitude.track({
                  event: 'Theme Changed',
                  properties: { toTheme: theme },
                })
              }
            />
            <LanguageSwitcher
              onLanguageChange={(language) =>
                analytics.amplitude.track({
                  event: 'Language Changed',
                  properties: { toLanguage: language },
                })
              }
            />
          </div>
        </div>

        <div className={styles.skeletonContent}>
          <Skeleton />
        </div>

        <ChatInput
          name='send-new-message-form'
          isLoading={isSendMessageMutationPending}
          onSubmit={() => {}}
        />
      </div>
    );
  }

  // Handle session not found
  if (!session) {
    return (
      <div className={styles.mainChat}>
        <div className={styles.errorState}>
          <h2>{t('conversation.messages.notFound')}</h2>
          <p>{t('conversation.messages.notFoundDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <LogAmplitudeEventOnMount
        event='Conversation Page Viewed'
        properties={{ chatId: session.id, model: session.appName }}
      />

      <div className={styles.mainChat}>
        <div className={styles.header}>
          <div className={styles.headerActions}>
            <SidebarTrigger />

            <CurrentModel session={session!} />
          </div>

          <div className={styles.headerActions}>
            <ThemeSwitcher
              onThemeChange={(theme) =>
                analytics.amplitude.track({
                  event: 'Theme Changed',
                  properties: { toTheme: theme },
                })
              }
            />
            <LanguageSwitcher
              onLanguageChange={(language) =>
                analytics.amplitude.track({
                  event: 'Language Changed',
                  properties: { toLanguage: language },
                })
              }
            />
          </div>
        </div>

        <ChatMessages
          className={styles.chatMessages}
          session={session!}
          isAgentResponding={isSendMessageMutationPending}
        />

        <div className={styles.footer}>
          <ChatInput
            name='send-new-message-form'
            isLoading={isSendMessageMutationPending}
            onSubmit={(text) => {
              if (text.length > 0) {
                sendMessageMutation.mutate({
                  sessionId,
                  text,
                  appName: session.appName,
                  type: 'send',
                  from: 'text',
                });
              }
            }}
          />
        </div>
      </div>
    </>
  );
});

export { Conversation };
